import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import DataTable, { downloadBlob } from "@/components/DataTable";
import CommercialStatusBadge from "@/components/CommercialStatusBadge";
import { Button } from "@/components/ui/button";
import { Plus, History } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const CREATE_ROLES = new Set(["sales", "plant_head", "admin", "super_admin"]);

export default function OaList() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);

  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)); }, []);

  const fetchData = React.useCallback(async ({ skip, limit, sort, search, filters }) => {
    const params = { skip, limit, sort, search };
    if (filters.status) params.status = filters.status;
    if (filters.customer_id) params.customer_id = filters.customer_id;
    const { data } = await api.get("/order-acceptances", { params });
    return data;
  }, []);

  async function handleExport() {
    const token = localStorage.getItem("hispl_token");
    const res = await fetch(`${API}/order-acceptances/export`, { headers: { Authorization: `Bearer ${token}` } });
    await downloadBlob(res, "order_acceptances.xlsx");
  }

  const canCreate = user && CREATE_ROLES.has(user.role);

  const columns = [
    { key: "oa_number", label: "OA #", sortable: true,
      render: (r) => <span className="font-mono text-[12px] font-semibold text-blue-900">{r.oa_number}</span> },
    { key: "po_number", label: "PO #",
      render: (r) => <span className="font-mono text-xs text-slate-600">{r.customer_po_number || r.po_number || "—"}</span> },
    { key: "oa_date", label: "Date", sortable: true, className: "font-mono text-xs text-slate-600" },
    { key: "customer_name", label: "Customer", className: "text-slate-800" },
    { key: "project_name", label: "Project",
      render: (r) => <span className="text-xs text-slate-700">{r.project_name || "—"}</span> },
    { key: "delivery_commitment", label: "Delivery By",
      render: (r) => <span className="font-mono text-xs text-slate-600">{r.delivery_commitment || "—"}</span> },
    { key: "line_count", label: "Lines",
      render: (r) => <span className="font-mono text-xs">{r.line_count}</span> },
    { key: "total_qty", label: "Total Qty",
      render: (r) => <span className="font-mono text-xs font-semibold">{r.total_qty}</span> },
    { key: "total_value", label: "Value ₹", visibleByDefault: false,
      render: (r) => <span className="font-mono text-xs font-semibold">₹{(r.total_value || 0).toLocaleString("en-IN")}</span> },
    { key: "historical", label: "Type",
      render: (r) => (
        <span className={`text-[10px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 border ${
          r.historical ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-50 text-slate-700 border-slate-200"
        }`}>{r.historical ? "Historical" : "Live"}</span>
      ) },
    { key: "status", label: "Status", sortable: true,
      render: (r) => <CommercialStatusBadge status={r.status} /> },
  ];

  return (
    <div data-testid="oa-list-page" className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">Commercial Workflow · Stage 4 · Final</div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>Order Acceptances</h1>
          <p className="text-sm text-slate-500 mt-1">Confirmed orders ready to be released to production. Cylinder numbers are born from here.</p>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="btn-from-po-oa"
                    onClick={() => nav("/purchase-orders?status=Received")}
                    title="Open the POs list and click 'Create Order Acceptance' on a Received PO to pre-fill from the PO.">
              <History className="w-3.5 h-3.5 mr-1.5" /> From Purchase Order
            </Button>
            <Button size="sm" data-testid="btn-create-oa"
                    onClick={() => nav("/order-acceptances/new")}
                    title="Create a new Order Acceptance directly. Only Customer is required; every other reference can be linked later."
                    className="bg-red-800 hover:bg-red-900 text-white">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Create New OA
            </Button>
          </div>
        )}
      </div>

      <DataTable
        columns={columns} fetchData={fetchData} onExport={handleExport}
        filters={[
          { key: "status", label: "Status",
            options: ["Draft", "Confirmed", "ReleasedToProduction", "OnHold", "Cancelled", "Closed"].map((s) => ({ value: s, label: s })) },
          { key: "customer_id", label: "Customer",
            options: customers.map((c) => ({ value: c.id, label: c.name })) },
        ]}
        storageKey="oas"
        onRowClick={(r) => nav(`/order-acceptances/${r.id}`)}
        emptyMessage="No order acceptances yet. Create one from a Received purchase order or enter a Historical OA."
      />
    </div>
  );
}
